import type { VisualEffect } from '../tilemap/types';

const DURATION_MS = 500;

export class SmiteEffect implements VisualEffect {
    private target: { x: number; y: number };
    private tileToScreenCenter: (x: number, y: number) => { x: number; y: number };
    private tileSize: number;

    private elapsed: number = 0;
    private isFinished: boolean = false;

    // Pre-generate jitter points so the bolt shape is stable per frame
    private boltJitter: number[];

    constructor(options: {
        target: { x: number; y: number };
        tileToScreenCenter: (x: number, y: number) => { x: number; y: number };
        tileSize: number;
    }) {
        this.target = options.target;
        this.tileToScreenCenter = options.tileToScreenCenter;
        this.tileSize = options.tileSize;

        // 5 zigzag segments — pre-bake offsets so the bolt doesn't shimmer
        this.boltJitter = Array.from({ length: 5 }, () => (Math.random() - 0.5) * 14);
    }

    public update(deltaSeconds: number): void {
        if (this.isFinished) return;
        this.elapsed += deltaSeconds * 1000;
        if (this.elapsed >= DURATION_MS) this.isFinished = true;
    }

    public renderOverlay(ctx: CanvasRenderingContext2D): void {
        if (this.isFinished) return;

        const progress = Math.min(this.elapsed / DURATION_MS, 1);
        const screen = this.tileToScreenCenter(this.target.x, this.target.y);

        // Bolt comes from well above the target
        const boltTopY = screen.y - this.tileSize * 1.25;
        const boltBottomY = screen.y;
        const boltX = screen.x;

        // Strike phase: bolt visible for first 40%, then fades
        const strikeProgress = Math.min(progress / 0.4, 1);
        const fadeProgress = progress > 0.4 ? (progress - 0.4) / 0.6 : 0;
        const boltAlpha = (1 - fadeProgress) * Math.min(strikeProgress * 4, 1);

        // Impact flash — quick bright circle at the target, fades fast
        const flashAlpha = Math.max(0, 1 - progress / 0.25);
        if (flashAlpha > 0) {
            const flashRadius = this.tileSize * 0.7 * (1 + progress * 0.5);
            const gradient = ctx.createRadialGradient(boltX, boltBottomY, 0, boltX, boltBottomY, flashRadius);
            gradient.addColorStop(0, `rgba(255, 255, 220, ${flashAlpha * 0.9})`);
            gradient.addColorStop(0.4, `rgba(200, 220, 255, ${flashAlpha * 0.5})`);
            gradient.addColorStop(1, 'rgba(100, 150, 255, 0)');
            ctx.save();
            ctx.fillStyle = gradient;
            ctx.beginPath();
            ctx.arc(boltX, boltBottomY, flashRadius, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
        }

        if (boltAlpha <= 0) return;

        // Build zigzag bolt path from top to bottom
        const segmentCount = this.boltJitter.length;
        const points: { x: number; y: number }[] = [];
        points.push({ x: boltX, y: boltTopY });
        for (let i = 0; i < segmentCount; i++) {
            const t = (i + 1) / (segmentCount + 1);
            points.push({
                x: boltX + this.boltJitter[i],
                y: boltTopY + (boltBottomY - boltTopY) * t,
            });
        }
        points.push({ x: boltX, y: boltBottomY });

        ctx.save();
        ctx.globalAlpha = boltAlpha;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        // Glow layers: wide soft outer → narrow bright core
        const layers = [
            { width: 22, color: 'rgba(255, 220, 50, 0.15)' },
            { width: 12, color: 'rgba(255, 210, 0, 0.45)' },
            { width: 6,  color: 'rgba(255, 240, 100, 0.9)' },
            { width: 2.5, color: 'rgba(255, 255, 220, 1.0)' },
        ];

        for (const layer of layers) {
            ctx.beginPath();
            ctx.lineWidth = layer.width;
            ctx.strokeStyle = layer.color;
            ctx.moveTo(points[0].x, points[0].y);
            for (let i = 1; i < points.length; i++) {
                ctx.lineTo(points[i].x, points[i].y);
            }
            ctx.stroke();
        }

        ctx.restore();
    }

    public isComplete(): boolean {
        return this.isFinished;
    }
}
